/**
 * Authentication helper functions for Wildlife Migration Tracker Admin
 * Simple password-based auth with localStorage persistence
 */

// Session token key for localStorage
const SESSION_TOKEN_KEY = 'wmt_admin_session';
const SESSION_USER_KEY = 'wmt_admin_user';
const SESSION_EXPIRY_KEY = 'wmt_admin_expiry';

// Session duration: 24 hours
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;

/**
 * Generate a simple session token
 * Uses timestamp + random string for uniqueness
 * @returns {string} Session token
 */
export function generateSessionToken() {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  const randomPart2 = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomPart}-${randomPart2}`;
}

/**
 * Validate password against environment variable
 * @param {string} password - Password to validate
 * @returns {boolean} True if password matches
 */
export function validatePassword(password) {
  const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD;

  if (!adminPassword) {
    console.error('VITE_ADMIN_PASSWORD not set in environment variables');
    return false;
  }

  return password === adminPassword;
}

/**
 * Create and store a new session
 * @param {string} username - Username for the session
 * @returns {object} Session object with token and expiry
 */
export function createSession(username) {
  const token = generateSessionToken();
  const expiry = Date.now() + SESSION_DURATION_MS;

  localStorage.setItem(SESSION_TOKEN_KEY, token);
  localStorage.setItem(SESSION_USER_KEY, username);
  localStorage.setItem(SESSION_EXPIRY_KEY, expiry.toString());

  return { token, username, expiry };
}

/**
 * Get current session from localStorage
 * @returns {object|null} Session object or null if not found
 */
export function getSession() {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  const username = localStorage.getItem(SESSION_USER_KEY);
  const expiry = localStorage.getItem(SESSION_EXPIRY_KEY);

  if (!token || !username || !expiry) {
    return null;
  }

  return {
    token,
    username,
    expiry: parseInt(expiry, 10)
  };
}

/**
 * Check if current session is valid (exists and not expired)
 * @returns {boolean} True if session is valid
 */
export function isSessionValid() {
  const session = getSession();

  if (!session) {
    return false;
  }

  // Check if session has expired
  if (Date.now() > session.expiry) {
    clearSession();
    return false;
  }

  return true;
}

/**
 * Clear the current session from localStorage
 */
export function clearSession() {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_USER_KEY);
  localStorage.removeItem(SESSION_EXPIRY_KEY);
}

/**
 * Refresh session expiry (extends session by SESSION_DURATION_MS)
 * @returns {boolean} True if session was refreshed, false if no valid session
 */
export function refreshSession() {
  if (!isSessionValid()) {
    return false;
  }

  const newExpiry = Date.now() + SESSION_DURATION_MS;
  localStorage.setItem(SESSION_EXPIRY_KEY, newExpiry.toString());
  return true;
}

/**
 * Get remaining session time in milliseconds
 * @returns {number} Remaining time in ms, or 0 if no valid session
 */
export function getSessionTimeRemaining() {
  const session = getSession();

  if (!session) {
    return 0;
  }

  const remaining = session.expiry - Date.now();
  return remaining > 0 ? remaining : 0;
}
